import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useProgramAsync } from "@/modules/programs/hooks/usePrograms"
import { useSidebar } from "@/modules/sidebar/hooks/useSidebar"
import { useTemplateSessionImportSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import { CalendarArrowUpIcon, PlusIcon } from "lucide-react"
import { useParams } from "react-router-dom"

type AddSessionButtonProps = {
  dateSession: Date | number
  onAddEventClick: (arg0?: number | Date | undefined) => void
  className?: string
} & React.ComponentProps<"div">

export function AddSessionButton({ ...props }: AddSessionButtonProps) {
  const { programId, templateId } = useParams()

  if (programId) {
    return <AddProgramSessionButton programId={programId} {...props} />
  } else if (templateId) {
    return <AddTemplateSessionButton {...props} />
  }
  return null
}

function AddProgramSessionButton({
  programId,
  dateSession,
  onAddEventClick,
  className,
  ...props
}: AddSessionButtonProps & { programId: string }) {
  const { isSidebarVisible } = useSidebar()
  const { navigateToTemplateSessionImport } = useTemplateSessionImportSidebar()
  const { data: program } = useProgramAsync(programId)

  if (!program) return null

  return (
    <div
      className={cn(
        "add flex flex-col justify-center gap-4 rounded-xl bg-surface-background-secondary px-4 pt-6 pb-8 text-center",
        { "add-hidden cursor-default! *:pointer-events-none": isSidebarVisible },
        className,
      )}
      {...props}
    >
      <div className="flex flex-col items-center justify-center">
        <PlusIcon color="currentColor" width={32} height={32} />
        <Text fontStyle="TM">{word("planning.addSession.button")}</Text>
        <Text fontStyle="BXS">{word("sessionForm.create.subtitle")}</Text>
      </div>
      <div className="flex flex-col items-center justify-center gap-2">
        <Button
          variant="secondary"
          size="md"
          className="w-full"
          onClick={() => {
            navigateToTemplateSessionImport(
              program.id,
              {
                date: dateSession instanceof Date ? dateSession : null,
                index: typeof dateSession === "number" ? dateSession : null,
                virtualProgramByDateIndex: null,
              },
              { replaceAllComponents: true },
            )
          }}
        >
          <CalendarArrowUpIcon color="currentColor" width={16} height={16} />
          {word("global.template.plural", { count: 1 })}
        </Button>
        <Button
          variant="primary"
          size="md"
          className="w-full"
          onClick={() => {
            const value =
              dateSession instanceof Date ? dateSession : typeof dateSession === "number" ? dateSession : undefined
            onAddEventClick(value)
          }}
        >
          <PlusIcon color="currentColor" width={16} height={16} />
          {word("global.session.plural", { count: 1 })}
        </Button>
      </div>
    </div>
  )
}
function AddTemplateSessionButton({ dateSession, onAddEventClick, className, ...props }: AddSessionButtonProps) {
  const { isSidebarVisible } = useSidebar()

  return (
    <div
      className={cn(
        "add flex flex-col justify-center gap-4 rounded-xl bg-surface-background-secondary px-4 pt-6 pb-8 text-center",
        { "add-hidden cursor-default! *:pointer-events-none": isSidebarVisible },
        className,
      )}
      {...props}
    >
      <div className="flex flex-col items-center justify-center">
        <PlusIcon color="currentColor" width={32} height={32} />
        <Text fontStyle="TM">{word("planning.addSession.button")}</Text>
        <Text fontStyle="BXS">{word("sessionForm.create.subtitle")}</Text>
      </div>
      <div className="flex flex-col items-center justify-center gap-2">
        <Button
          variant="primary"
          size="md"
          className="w-full"
          onClick={() => {
            if (typeof dateSession === "number") onAddEventClick(dateSession)
            else
              throw new Error(
                "dateSession should be a number representing the index of the session in the template when adding a session from the template calendar",
              )
          }}
        >
          <PlusIcon color="currentColor" width={16} height={16} />
          {word("global.session.plural", { count: 1 })}
        </Button>
      </div>
    </div>
  )
}
